import React, {
  useState,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import {
  Box,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Checkbox,
  FormControlLabel,
  Typography,
  Snackbar,
  Alert,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import CategoryDropdown from "./CategoryDropdown";
import ExpandableTariffTable from "../components/ExpandableTariffTable"; // Adjust path as needed
import axiosInstance from "../api/axiosInstance";

/* ------------------------------------------------------------------ */
/* Data definitions                                                    */
/* ------------------------------------------------------------------ */
const initialSlabObject = {
  locationId: "",
  vehicleGroupId: "",
  sale_os_km_rate: "",
  sale_os_min_kms: "",
  sale_os_day_rate: "",
  sale_driver_batta: "",
  sale_night_batta: "",
  sale_batta_percent: "",
  sale_airport_transfer_rate: "",
  sale_fuel_escalation_per_km: "",
  sale_extra_km_rate: "",
  sale_extra_hour_rate: "",
  purchase_os_km_rate: "",
  purchase_os_min_kms: "",
  purchase_os_day_rate: "",
  purchase_driver_batta: "",
  purchase_night_batta: "",
  purchase_batta_percent: "",
  purchase_airport_transfer_rate: "",
  purchase_fuel_escalation_per_km: "",
  purchase_extra_km_rate: "",
  purchase_extra_hour_rate: "",
  hours: "",
  kms: "",
  sale_slab_rate: "0",
  purchase_slab_rate: "0",
  sale_grace_time: "",
  purchase_grace_time: "",
  slab_da: "",
  da_extra_per_hour: "",
  skip_slab: false,
};

// Input fields (rates card)
const saleFields = [
  { label: "OS Km Rate", field: "sale_os_km_rate" },
  { label: "OS Min Kms", field: "sale_os_min_kms" },
  { label: "OS Day Rate", field: "sale_os_day_rate" },
  { label: "Batta", field: "sale_driver_batta" },
  { label: "Night Batta", field: "sale_night_batta" },
  { label: "Batta %", field: "sale_batta_percent" },
  { label: "Airport", field: "sale_airport_transfer_rate" },
  { label: "Fuel Escalation", field: "sale_fuel_escalation_per_km" },
  { label: "Extra Km", field: "sale_extra_km_rate" },
  { label: "Extra Hour", field: "sale_extra_hour_rate" },
];

const purchaseFields = [
  { label: "OS Km Rate", field: "purchase_os_km_rate" },
  { label: "OS Min Kms", field: "purchase_os_min_kms" },
  { label: "OS Day Rate", field: "purchase_os_day_rate" },
  { label: "Driver Batta", field: "purchase_driver_batta" },
  { label: "Night Batta", field: "purchase_night_batta" },
  { label: "Batta %", field: "purchase_batta_percent" },
  { label: "Airport", field: "purchase_airport_transfer_rate" },
  { label: "Fuel Escalation", field: "purchase_fuel_escalation_per_km" },
  { label: "Extra Km", field: "purchase_extra_km_rate" },
  { label: "Extra Hour", field: "purchase_extra_hour_rate" },
];

// Slab input fields
const commonslabFields = [
  { label: "Hours", field: "hours", type: "number" },
  { label: "KMs", field: "kms", type: "number" },
];

const salesslabFields = [
  { label: "Slab Rate", field: "sale_slab_rate", type: "number" },
  { label: "Grace Time", field: "sale_grace_time", type: "number" },
];

const purchaseslabFields = [
  { label: "Slab Rate", field: "purchase_slab_rate", type: "number" },
  { label: "Grace Time", field: "purchase_grace_time", type: "number" },
  { label: "DA", field: "slab_da", type: "number" },
  { label: "DA/Hour", field: "da_extra_per_hour", type: "number" },
  { label: "Skip", field: "skip_slab", type: "checkbox" },
];

// Columns shown in the rates summary table
const saleTableCols = [
  ["OS Km Rate", "sale_os_km_rate"],
  ["OS Min Kms", "sale_os_min_kms"],
  ["OS Day Rate", "sale_os_day_rate"],
  ["Batta", "sale_driver_batta"],
  ["Airport", "sale_airport_transfer_rate"],
  ["Extra Km", "sale_extra_km_rate"],
  ["Extra Hour", "sale_extra_hour_rate"],
];

const purchaseTableCols = [
  ["OS Km Rate", "purchase_os_km_rate"],
  ["OS Min Kms", "purchase_os_min_kms"],
  ["OS Day Rate", "purchase_os_day_rate"],
  ["Driver Batta", "purchase_driver_batta"],
  ["Airport", "purchase_airport_transfer_rate"],
  ["Extra Km", "purchase_extra_km_rate"],
  ["Extra Hour", "purchase_extra_hour_rate"],
];

/* ------------------------------------------------------------------ */
/* Compact design tokens                                               */
/* ------------------------------------------------------------------ */
const C = {
  primary: "#1d4ed8",
  primaryHover: "#1e40af",
  border: "#e2e8f0",
  surface: "#f8fafc",
  selected: "#eff6ff",
  text: "#0f172a",
  muted: "#64748b",
};

const inputSx = {
  "& .MuiInputBase-root": { fontSize: 13, backgroundColor: "#fff" },
  "& .MuiInputBase-input": { py: "6px", px: "8px" },
  "& .MuiInputLabel-root": { fontSize: 12 },
  "& .MuiInputLabel-shrink": { fontSize: 12 },
};

const numberInputSx = {
  ...inputSx,
  "& input": { textAlign: "right" },
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
};

const btnBase = {
  textTransform: "none",
  fontSize: 13,
  fontWeight: 500,
  borderRadius: "6px",
  boxShadow: "none",
  minWidth: 0,
  px: 2,
  py: 0.5,
  backgroundColor: C.primary,
  "&:hover": { backgroundColor: C.primaryHover, boxShadow: "none" },
};

const thSx = {
  fontSize: 12,
  fontWeight: 600,
  color: C.muted,
  textAlign: "center",
  whiteSpace: "nowrap",
  backgroundColor: C.surface,
  border: `1px solid ${C.border}`,
  px: 1,
  py: 0.75,
};

const tdSx = {
  fontSize: 12.5,
  textAlign: "center",
  whiteSpace: "nowrap",
  border: `1px solid ${C.border}`,
  px: 1,
  py: 0.5,
};

/* ------------------------------------------------------------------ */
/* Presentational helpers                                              */
/* ------------------------------------------------------------------ */
const Card = ({ title, subtitle, action, children }) => (
  <Box
    sx={{
      border: `1px solid ${C.border}`,
      borderRadius: "8px",
      backgroundColor: "#fff",
      mb: 2,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        px: 2,
        py: 1,
        minHeight: 44,
        borderBottom: `1px solid ${C.border}`,
        backgroundColor: C.surface,
        borderRadius: "8px 8px 0 0",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 1 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 12, color: C.muted }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
    <Box sx={{ p: 2 }}>{children}</Box>
  </Box>
);

const GroupLabel = ({ children }) => (
  <Typography
    sx={{ fontSize: 12, fontWeight: 600, color: C.primary, mb: 0.75 }}
  >
    {children}
  </Typography>
);

const fieldGridSx = {
  display: "grid",
  gap: 1,
  gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))",
};

const Empty = ({ children }) => (
  <Typography
    sx={{ fontSize: 13, color: C.muted, textAlign: "center", py: 2.5 }}
  >
    {children}
  </Typography>
);

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
const TariffManager = React.forwardRef(
  (
    {
      entityType,
      entityId,
      clientId,
      token,
      readOnly = false,
      onTariffsChange,
    },
    ref
  ) => {
    const [selectedVehicleGroup, setSelectedVehicleGroup] = useState("");
    const [selectedLocation, setSelectedLocation] = useState("");

    // { [locationId]: { [vehicleGroupId]: { mainFields, slabs } } }
    const [tariffsByLocation, setTariffsByLocation] = useState({});

    // Slab/rate currently being edited in the inputs
    const [editingSlab, setEditingSlab] = useState({ ...initialSlabObject });
    const [editingIndex, setEditingIndex] = useState(null);

    const [showAll, setShowAll] = useState(false);
    const [vehicleGroupsOptions, setVehicleGroupsOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [notice, setNotice] = useState(null); // { message, severity }

    const isCustomer = entityType === "customer";

    /* ---------------------------- Lookups ---------------------------- */
    useEffect(() => {
      const fetchLocations = async () => {
        try {
          const res = await axiosInstance.get("/api/category", {
            params: { categorytype: "Location" },
          });
          setLocationOptions(res.data || []);
        } catch (err) {
          console.error(err);
          setLocationOptions([]);
        }
      };
      if (clientId && token) fetchLocations();
    }, [clientId, token]);

    useEffect(() => {
      const fetchVehicleGroups = async () => {
        try {
          const res = await axiosInstance.get("/api/category", {
            params: { categorytype: "VehicleGroup" },
          });
          setVehicleGroupsOptions(res.data || []);
        } catch (err) {
          console.error(err);
          setVehicleGroupsOptions([]);
        }
      };
      if (clientId && token) fetchVehicleGroups();
    }, [clientId, token]);

    const locationLabel = (id) => {
      if (!locationOptions || locationOptions.length === 0) return id;
      const found = locationOptions.find(
        (o) => String(o.categoryid) === String(id)
      );
      return found ? found.categoryname : id;
    };

    const vehicleGroupLabel = (id) => {
      const group = vehicleGroupsOptions.find(
        (option) => Number(option.categoryid) === Number(id)
      );
      return group ? group.categoryname : id;
    };

    const vehicleGroupOptionsWithNone = [
      { categoryid: -1, categoryname: "Select Vehicle Group" },
      ...vehicleGroupsOptions,
    ];

    /* ------------------------ Load saved tariffs --------------------- */
    useEffect(() => {
      if (!clientId || !entityType || !entityId || !token) return;

      (async () => {
        const res = await fetch(
          `/api/tariffs/${entityType}/${entityId}?client_id=${clientId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) return;
        const rows = await res.json();

        // Rebuild tariffsByLocation
        const byLoc = {};
        for (const t of rows) {
          const locId = String(t.locationid ?? "");
          const vgId = String(t.vehicle_group_id ?? t.vehiclegroupid ?? "");
          byLoc[locId] ||= {};
          byLoc[locId][vgId] = {
            mainFields: {
              tariff_id: t.tariff_id,
              sale_os_km_rate: t.sale_os_km_rate,
              sale_os_min_kms: t.sale_os_min_kms,
              sale_os_day_rate: t.sale_os_day_rate,
              sale_driver_batta: t.sale_driver_batta,
              sale_airport_transfer_rate: t.sale_airport_transfer_rate,
              sale_extra_km_rate: t.sale_extra_km_rate,
              sale_extra_hour_rate: t.sale_extra_hour_rate,
              purchase_os_km_rate: t.purchase_os_km_rate,
              purchase_os_min_kms: t.purchase_os_min_kms,
              purchase_os_day_rate: t.purchase_os_day_rate,
              purchase_driver_batta: t.purchase_driver_batta,
              purchase_airport_transfer_rate: t.purchase_airport_transfer_rate,
              purchase_extra_km_rate: t.purchase_extra_km_rate,
              purchase_extra_hour_rate: t.purchase_extra_hour_rate,
            },
            slabs: Array.isArray(t.slabs) ? t.slabs : [],
          };
        }
        setTariffsByLocation(byLoc);
      })();
    }, [clientId, entityType, entityId, token]);

    /* ------------------- Reset inputs on selection ------------------- */
    useEffect(() => {
      setEditingSlab({
        ...initialSlabObject,
        locationId: selectedLocation || "",
        vehicleGroupId: selectedVehicleGroup || "",
      });
      setEditingIndex(null);
    }, [selectedLocation, selectedVehicleGroup]);

    /* ---------------------------- Handlers --------------------------- */
    const notify = (message, severity = "info") =>
      setNotice({ message, severity });

    const handleFieldChange = (field, value) =>
      setEditingSlab((prev) => ({ ...prev, [field]: value }));

    const resetSlabInputs = () => {
      setEditingSlab({
        ...initialSlabObject,
        locationId: selectedLocation,
        vehicleGroupId: selectedVehicleGroup,
      });
      setEditingIndex(null);
    };

    // Save main rates for the selected location + vehicle group
    const handleSaveMainTariff = () => {
      if (!selectedLocation) {
        notify("Select a location to save rates", "warning");
        return;
      }
      if (!selectedVehicleGroup) {
        notify("Select a vehicle group to save rates", "warning");
        return;
      }

      const mainFieldsToSave = {};
      saleFields.forEach(({ field }) => {
        mainFieldsToSave[field] = editingSlab[field] || "";
      });
      purchaseFields.forEach(({ field }) => {
        mainFieldsToSave[field] = editingSlab[field] || "";
      });

      setTariffsByLocation((prev) => {
        const byVg = prev[selectedLocation] || {};
        const group = byVg[selectedVehicleGroup] || {
          mainFields: {},
          slabs: [],
        };
        return {
          ...prev,
          [selectedLocation]: {
            ...byVg,
            [selectedVehicleGroup]: {
              mainFields: { ...group.mainFields, ...mainFieldsToSave },
              slabs: Array.isArray(group.slabs) ? group.slabs : [],
            },
          },
        };
      });

      notify("Rates saved", "success");
    };

    const loadVehicleGroupForEdit = (vehicleGroupId) => {
      const group = tariffsByLocation[selectedLocation]?.[vehicleGroupId];
      if (group) {
        setSelectedVehicleGroup(vehicleGroupId);
        setEditingSlab({
          ...initialSlabObject,
          ...group.mainFields,
          locationId: selectedLocation,
          vehicleGroupId,
        });
        setEditingIndex(null);
      }
    };

    const handleDeleteVehicleGroup = (vehicleGroupId) => {
      if (!selectedLocation) return;
      if (
        !window.confirm(
          `Are you sure you want to delete ${locationLabel(
            selectedLocation
          )} / ${vehicleGroupLabel(vehicleGroupId)} tariffs?`
        )
      )
        return;

      setTariffsByLocation((prev) => {
        const copy = { ...prev };
        if (copy[selectedLocation]) {
          const inner = { ...copy[selectedLocation] };
          delete inner[vehicleGroupId];
          copy[selectedLocation] = inner;
        }
        return copy;
      });
    };

    const handleAddSlab = () => {
      if (!selectedLocation || !selectedVehicleGroup) {
        notify("Select a location and vehicle group first", "warning");
        return;
      }

      setTariffsByLocation((prev) => {
        const byVg = prev[selectedLocation] || {};
        const group = byVg[selectedVehicleGroup] || {
          mainFields: {},
          slabs: [],
        };
        const slab = {
          ...editingSlab,
          locationId: selectedLocation,
          vehicleGroupId: selectedVehicleGroup,
        };
        const updatedSlabs =
          editingIndex !== null
            ? group.slabs.map((s, i) => (i === editingIndex ? slab : s))
            : [...group.slabs, slab];

        return {
          ...prev,
          [selectedLocation]: {
            ...byVg,
            [selectedVehicleGroup]: {
              mainFields: group.mainFields, // keep mainFields unchanged here
              slabs: updatedSlabs,
            },
          },
        };
      });

      resetSlabInputs();
    };

    const handleEditSlab = (index) => {
      const slab = (tariffsByLocation[selectedLocation]?.[selectedVehicleGroup]
        ?.slabs || [])[index];
      if (slab) {
        setEditingSlab(slab);
        setEditingIndex(index);
      }
    };

    const handleDeleteSlab = (index) => {
      if (!window.confirm(`Delete slab #${index + 1}?`)) return;
      setTariffsByLocation((prev) => {
        const byVg = prev[selectedLocation] || {};
        const group = byVg[selectedVehicleGroup] || {
          mainFields: {},
          slabs: [],
        };
        return {
          ...prev,
          [selectedLocation]: {
            ...byVg,
            [selectedVehicleGroup]: {
              mainFields: group.mainFields,
              slabs: group.slabs.filter((_, i) => i !== index),
            },
          },
        };
      });
      if (editingIndex === index) resetSlabInputs();
    };

    /* ------------------------ Derived / exposed ---------------------- */
    // Payload sent to the parent via onTariffsChange
    const tariffsForSave = useMemo(() => {
      return Object.entries(tariffsByLocation).flatMap(([locId, byVg]) =>
        Object.entries(byVg).map(([vgId, data]) => ({
          client_id: clientId,
          entity_type: entityType,
          entity_id: entityId,
          locationid: Number(locId),
          location: locationLabel(locId),
          vehicle_group_id: Number(vgId),
          ...data.mainFields,
          slabs: Array.isArray(data.slabs) ? data.slabs : [],
          tariff_id: data.mainFields?.tariff_id ?? undefined,
        }))
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tariffsByLocation, clientId, entityType, entityId, locationOptions]);

    // Flattened rows (one per slab, or one per tariff without slabs)
    const allTariffs = Object.entries(tariffsByLocation).flatMap(
      ([locId, byVg]) =>
        Object.entries(byVg).flatMap(([vgId, data]) => {
          const slabs = Array.isArray(data.slabs) ? data.slabs : [];
          if (slabs.length === 0) {
            return [{ locationId: locId, vehicleGroupId: vgId, ...data.mainFields }];
          }
          return slabs.map((slab) => ({
            locationId: locId,
            vehicleGroupId: vgId,
            ...data.mainFields,
            ...slab,
          }));
        })
    );

    // Expose functions to parent via ref
    useImperativeHandle(ref, () => ({
      getTariffs: () => allTariffs,
      resetTariffs: () => setTariffsByLocation({}),
    }));

    useEffect(() => {
      onTariffsChange?.(tariffsForSave);
    }, [tariffsForSave, onTariffsChange]);

    const currentGroups = tariffsByLocation[selectedLocation] || {};
    const currentSlabs =
      tariffsByLocation?.[selectedLocation]?.[selectedVehicleGroup]?.slabs ||
      [];

    /* ----------------------------- Render ---------------------------- */
    // Number input bound to editingSlab (render function, not a component,
    // so inputs keep focus while typing)
    const numInput = ({ label, field }) => {
      const raw = editingSlab[field];
      const value = raw !== undefined && raw !== null && raw !== "" ? raw : "0";
      return (
        <TextField
          key={field}
          label={label}
          type="number"
          size="small"
          disabled={readOnly}
          value={value}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          sx={numberInputSx}
        />
      );
    };

    const cellValue = (slab, { field, type }) =>
      type === "checkbox" ? (
        <Checkbox checked={!!slab[field]} disabled size="small" sx={{ p: 0 }} />
      ) : (
        slab[field]
      );

    const slabGroups = [
      { title: "Slab", fields: commonslabFields, show: true },
      { title: "Customer", fields: salesslabFields, show: isCustomer },
      { title: "Vehicle / vendor", fields: purchaseslabFields, show: true },
    ];

    return (
      <Box sx={{ maxWidth: 1600, mx: "auto" }}>
        {/* ---------- Selection bar ---------- */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1.5,
            mb: 2,
          }}
        >
          <Box sx={{ width: 240 }}>
            <CategoryDropdown
              clientId={clientId}
              token={token}
              categoryType="Location"
              label="Location"
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              size="small"
              disabled={readOnly}
              sx={{ width: 240 }}
              SelectProps={{ sx: { width: 240, minWidth: 240 } }}
              InputProps={{ sx: { width: 240, minWidth: 240 } }}
            />
          </Box>
          <Box sx={{ width: 240 }}>
            <CategoryDropdown
              clientId={clientId}
              token={token}
              categoryType="VehicleGroup"
              label="Vehicle Group"
              options={vehicleGroupOptionsWithNone}
              value={selectedVehicleGroup ?? -1} // default to -1 (None)
              onChange={(e) => setSelectedVehicleGroup(Number(e.target.value))}
              size="small"
              disabled={readOnly}
              sx={{ width: 240 }}
              SelectProps={{ sx: { width: 240, minWidth: 240 } }}
              InputProps={{ sx: { width: 240, minWidth: 240 } }}
            />
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => setShowAll(true)}
            disabled={readOnly}
            sx={{
              ...btnBase,
              backgroundColor: "#fff",
              color: C.text,
              borderColor: C.border,
              "&:hover": { backgroundColor: C.surface, borderColor: C.border },
            }}
          >
            View all tariffs
          </Button>
        </Box>

        {/* ---------- Rates ---------- */}
        <Card
          title="Rates"
          subtitle={
            selectedLocation
              ? `${locationLabel(selectedLocation)}${
                  selectedVehicleGroup
                    ? ` · ${vehicleGroupLabel(selectedVehicleGroup)}`
                    : ""
                }`
              : "Select a location and vehicle group"
          }
          action={
            <Button
              variant="contained"
              onClick={handleSaveMainTariff}
              disabled={readOnly}
              sx={btnBase}
            >
              Save rates
            </Button>
          }
        >
          {isCustomer && (
            <Box sx={{ mb: 2 }}>
              <GroupLabel>Customer (sale)</GroupLabel>
              <Box sx={fieldGridSx}>{saleFields.map(numInput)}</Box>
            </Box>
          )}
          <GroupLabel>Vendor / vehicle (purchase)</GroupLabel>
          <Box sx={{ ...fieldGridSx, mb: 2 }}>{purchaseFields.map(numInput)}</Box>

          {/* Saved rates for this location */}
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: isCustomer ? 1100 : 700 }}>
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={{ ...thSx, minWidth: 140 }}>
                    Vehicle group
                  </TableCell>
                  {isCustomer && (
                    <TableCell colSpan={saleTableCols.length} sx={thSx}>
                      Customer
                    </TableCell>
                  )}
                  <TableCell colSpan={purchaseTableCols.length} sx={thSx}>
                    Vehicle / vendor
                  </TableCell>
                  <TableCell rowSpan={2} sx={{ ...thSx, width: 80 }}>
                    Actions
                  </TableCell>
                </TableRow>
                <TableRow>
                  {isCustomer &&
                    saleTableCols.map(([label]) => (
                      <TableCell key={`s-${label}`} sx={thSx}>
                        {label}
                      </TableCell>
                    ))}
                  {purchaseTableCols.map(([label]) => (
                    <TableCell key={`p-${label}`} sx={thSx}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(currentGroups).map(([vgId, data]) => {
                  const active = String(selectedVehicleGroup) === String(vgId);
                  return (
                    <TableRow
                      key={vgId}
                      hover
                      onClick={() => setSelectedVehicleGroup(vgId)}
                      sx={{
                        cursor: "pointer",
                        backgroundColor: active ? C.selected : "transparent",
                      }}
                    >
                      <TableCell sx={{ ...tdSx, textAlign: "left", fontWeight: 600 }}>
                        {vehicleGroupLabel(vgId)}
                      </TableCell>
                      {isCustomer &&
                        saleTableCols.map(([, field]) => (
                          <TableCell key={`s-${field}`} sx={tdSx}>
                            {data.mainFields[field]}
                          </TableCell>
                        ))}
                      {purchaseTableCols.map(([, field]) => (
                        <TableCell key={`p-${field}`} sx={tdSx}>
                          {data.mainFields[field]}
                        </TableCell>
                      ))}
                      <TableCell sx={tdSx}>
                        <IconButton
                          size="small"
                          color="primary"
                          disabled={readOnly}
                          onClick={(e) => {
                            e.stopPropagation();
                            loadVehicleGroupForEdit(vgId);
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={readOnly}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteVehicleGroup(vgId);
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {Object.keys(currentGroups).length === 0 && (
              <Empty>
                {selectedLocation
                  ? "No rates saved for this location yet."
                  : "Select a location to see its saved rates."}
              </Empty>
            )}
          </Box>
        </Card>

        {/* ---------- Slabs ---------- */}
        <Card
          title="Slabs"
          subtitle={editingIndex !== null ? `Editing slab #${editingIndex + 1}` : ""}
          action={
            <Box sx={{ display: "flex", gap: 1 }}>
              {editingIndex !== null && (
                <Button
                  variant="outlined"
                  color="inherit"
                  onClick={resetSlabInputs}
                  sx={{
                    ...btnBase,
                    backgroundColor: "#fff",
                    color: C.muted,
                    borderColor: C.border,
                    "&:hover": { backgroundColor: C.surface, borderColor: C.border },
                  }}
                >
                  Cancel
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                onClick={handleAddSlab}
                disabled={readOnly}
                sx={btnBase}
              >
                {editingIndex !== null ? "Update slab" : "Add slab"}
              </Button>
            </Box>
          }
        >
          {/* Slab inputs, grouped */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2.5, mb: 2 }}>
            {slabGroups
              .filter((g) => g.show)
              .map((g) => (
                <Box key={g.title}>
                  <GroupLabel>{g.title}</GroupLabel>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {g.fields.map((f) =>
                      f.type === "checkbox" ? (
                        <FormControlLabel
                          key={f.field}
                          sx={{
                            m: 0,
                            "& .MuiFormControlLabel-label": { fontSize: 13 },
                          }}
                          control={
                            <Checkbox
                              size="small"
                              checked={!!editingSlab[f.field]}
                              disabled={readOnly}
                              onChange={(e) =>
                                handleFieldChange(f.field, e.target.checked)
                              }
                            />
                          }
                          label="Skip slab"
                        />
                      ) : (
                        <Box key={f.field} sx={{ width: 105 }}>
                          {numInput(f)}
                        </Box>
                      )
                    )}
                  </Box>
                </Box>
              ))}
          </Box>

          {/* Slabs table */}
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small" sx={{ minWidth: isCustomer ? 1000 : 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={{ ...thSx, minWidth: 120 }}>
                    Location
                  </TableCell>
                  <TableCell rowSpan={2} sx={{ ...thSx, minWidth: 140 }}>
                    Vehicle group
                  </TableCell>

                  {isCustomer && (
                    <TableCell
                      colSpan={commonslabFields.length + salesslabFields.length}
                      sx={thSx}
                    >
                      Customer group
                    </TableCell>
                  )}

                  {/* Vehicle / vendor entities show Hours & KMs as standalone columns */}
                  {(entityType === "vehicle" || entityType === "vendor") &&
                    commonslabFields.map(({ label, field }) => (
                      <TableCell key={field} rowSpan={2} sx={thSx}>
                        {label}
                      </TableCell>
                    ))}

                  <TableCell colSpan={purchaseslabFields.length} sx={thSx}>
                    Vehicle / vendor group
                  </TableCell>

                  {!readOnly && (
                    <TableCell rowSpan={2} sx={{ ...thSx, width: 80 }}>
                      Actions
                    </TableCell>
                  )}
                </TableRow>
                <TableRow>
                  {isCustomer &&
                    [...commonslabFields, ...salesslabFields].map(
                      ({ label, field }) => (
                        <TableCell key={field} sx={thSx}>
                          {label}
                        </TableCell>
                      )
                    )}
                  {purchaseslabFields.map(({ label, field }) => (
                    <TableCell key={field} sx={thSx}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {currentSlabs.map((slab, idx) => (
                  <TableRow
                    key={idx}
                    sx={{
                      backgroundColor:
                        editingIndex === idx ? C.selected : "transparent",
                    }}
                  >
                    <TableCell sx={tdSx}>
                      {locationLabel(selectedLocation) || "Unknown"}
                    </TableCell>
                    <TableCell sx={{ ...tdSx, fontWeight: 600 }}>
                      {vehicleGroupLabel(selectedVehicleGroup)}
                    </TableCell>

                    {commonslabFields.map((f) => (
                      <TableCell key={f.field} sx={tdSx}>
                        {cellValue(slab, f)}
                      </TableCell>
                    ))}
                    {isCustomer &&
                      salesslabFields.map((f) => (
                        <TableCell key={f.field} sx={tdSx}>
                          {cellValue(slab, f)}
                        </TableCell>
                      ))}
                    {purchaseslabFields.map((f) => (
                      <TableCell key={f.field} sx={tdSx}>
                        {cellValue(slab, f)}
                      </TableCell>
                    ))}

                    {!readOnly && (
                      <TableCell sx={tdSx}>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEditSlab(idx)}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteSlab(idx)}
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {currentSlabs.length === 0 && (
              <Empty>
                {selectedLocation && selectedVehicleGroup
                  ? "No slabs yet. Fill in the fields above and click “Add slab”."
                  : "Select a location and vehicle group to manage slabs."}
              </Empty>
            )}
          </Box>
        </Card>

        {/* ---------- All tariffs modal ---------- */}
        {showAll && (
          <ExpandableTariffTable
            allTariffs={allTariffs.map((r) => ({
              ...r,
              locationName: locationLabel(r.locationId),
              vehicleGroupName: vehicleGroupLabel(r.vehicleGroupId),
            }))}
            vehicleGroupLabel={vehicleGroupLabel}
            locationOptions={locationOptions}
            locationLabel={locationLabel}
            onClose={() => setShowAll(false)}
          />
        )}

        {/* ---------- Notifications ---------- */}
        <Snackbar
          open={!!notice}
          autoHideDuration={3000}
          onClose={() => setNotice(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          {notice ? (
            <Alert
              severity={notice.severity}
              variant="filled"
              onClose={() => setNotice(null)}
              sx={{ fontSize: 13 }}
            >
              {notice.message}
            </Alert>
          ) : undefined}
        </Snackbar>
      </Box>
    );
  }
);

export default TariffManager;
